import React, { useCallback, useEffect, useState } from 'react';
import { Rocket, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, ExternalLink, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const STEPS = ['Plan', 'Import', 'Review', 'Onboarded'];
const ENTITY_LABELS = { property: 'Properties', unit: 'Units', vendor_account: 'Vendor Accounts', meter: 'Meters' };

const stepIndex = (status) => {
  if (status === 'complete') return 3;
  if (status === 'review') return 2;
  if (status === 'in_progress') return 1;
  return 0;
};

/**
 * Migration Journey card rendered inside the agent chat. Refresh-safe:
 * re-hydrates the journey phase from the backend on every mount.
 */
export const MigrationJourneyCard = ({ card }) => {
  const journeyId = card?.journey_id;
  const orgId = card?.org_id || getCurrentAccount()?.id || null;
  const [journey, setJourney] = useState(null);
  const [credits, setCredits] = useState(null);
  const [scrapeRun, setScrapeRun] = useState(null);
  const [twofa, setTwofa] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [approving, setApproving] = useState(false);

  const params = { params: { account_id: orgId } };

  const load = useCallback(() => {
    if (!journeyId || !orgId) {
      if (!orgId) setLoadError('No organisation is in scope for this journey.');
      return;
    }
    setLoadError(null);
    api.get(`/ubm-migration/journeys/${journeyId}`, params)
      .then(({ data }) => setJourney(data))
      .catch((e) => setLoadError(e.response?.data?.detail || 'Could not load the migration journey.'));
    api.get(`/ubm-migration/journeys/${journeyId}/credits`, params)
      .then(({ data }) => setCredits(data)).catch(() => {});
    api.get(`/ubm-migration/journeys/${journeyId}/runs`, params)
      .then(({ data }) => {
        const active = (data.scrape_runs || []).find((r) => ['queued', 'running', 'awaiting_2fa'].includes(r.status));
        setScrapeRun(active || (data.scrape_runs || [])[0] || null);
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, orgId]);

  useEffect(() => { load(); }, [load]);

  // Live-poll while a scrape bot is active.
  useEffect(() => {
    if (!scrapeRun || !['queued', 'running', 'awaiting_2fa'].includes(scrapeRun.status)) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [scrapeRun, load]);

  const submit2fa = async () => {
    if (!twofa.trim() || !scrapeRun) return;
    try {
      await api.post(`/ubm-migration/runs/${scrapeRun.id}/2fa`, { code: twofa.trim() }, params);
      toast.success('Verification code sent');
      setTwofa('');
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit code');
    }
  };

  const approve = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(`/ubm-migration/journeys/${journeyId}/approve`, {}, params);
      const c = data.created || {};
      toast.success(data.already_applied
        ? 'Already onboarded'
        : `Onboarded ${c.locations || 0} properties, ${c.vendor_accounts || 0} accounts`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboard failed');
    } finally {
      setApproving(false);
    }
  };

  const shell = (children) => (
    <div data-testid="ai-migration-journey-card" className="mt-3 rounded-lg border border-slate-200 bg-white p-3 not-prose">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900 mb-2">
        <Rocket className="h-4 w-4 text-orange-600" /> Platform Migration
        {journey?.plan?.source_platform && (
          <Badge variant="outline" className="rounded-none text-[10px]">{journey.plan.source_platform}</Badge>
        )}
      </div>
      {children}
    </div>
  );

  if (loadError) return shell(<div data-testid="migration-card-error" className="text-xs text-rose-600 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {loadError}</div>);
  if (!journey) return shell(<div className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading journey…</div>);

  const active = stepIndex(journey.status);
  const counts = journey.counts || {};
  const total = counts.all?.total || 0;
  const created = journey.result?.created || {};

  return shell(
    <div className="space-y-2.5">
      {/* Stepper */}
      <div className="flex items-center gap-1" data-testid="migration-card-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1 text-[11px] ${i <= active ? 'text-orange-700 font-semibold' : 'text-slate-400'}`}>
              <span className={`h-4 w-4 rounded-full text-[9px] flex items-center justify-center ${i < active || journey.status === 'complete' && i === 3 ? 'bg-emerald-500 text-white' : i === active ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {i < active || (journey.status === 'complete' && i === 3) ? '✓' : i + 1}
              </span>
              {s}
            </div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < active ? 'bg-orange-400' : 'bg-slate-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Staged counts */}
      {total > 0 && (
        <div className="grid grid-cols-4 gap-1.5" data-testid="migration-card-counts">
          {Object.entries(ENTITY_LABELS).map(([k, label]) => {
            const c = counts[k] || {};
            return (
              <div key={k} className="rounded border border-slate-100 bg-slate-50 p-1.5 text-center">
                <div className="text-sm font-bold text-slate-900">{c.total || 0}</div>
                <div className="text-[9px] text-slate-500 uppercase tracking-wide">{label}</div>
                {(c.exists || 0) > 0 && <div className="text-[9px] text-blue-600">{c.exists} existing</div>}
              </div>
            );
          })}
        </div>
      )}

      {journey.status === 'intake' && total === 0 && (
        <div className="text-[11px] text-slate-500">
          Attach your platform export (XLS/CSV) with the paperclip and I'll map &amp; stage it — nothing is onboarded without your review.
        </div>
      )}

      {/* Live scrape-bot status + 2FA relay */}
      {scrapeRun && ['queued', 'running', 'awaiting_2fa', 'timeout', 'failed'].includes(scrapeRun.status) && (
        <div className={`rounded-md border p-2 text-[11px] ${scrapeRun.status === 'awaiting_2fa' ? 'border-amber-300 bg-amber-50' : scrapeRun.status === 'timeout' || scrapeRun.status === 'failed' ? 'border-rose-200 bg-rose-50' : 'border-blue-200 bg-blue-50'}`} data-testid="migration-card-scrape">
          <div className="flex items-center gap-1.5">
            {['queued', 'running'].includes(scrapeRun.status) && <Loader2 className="h-3 w-3 animate-spin text-blue-600" />}
            {scrapeRun.status === 'awaiting_2fa' && <ShieldAlert className="h-3 w-3 text-amber-600" />}
            <span className="font-medium capitalize">{scrapeRun.status.replace('_', ' ')}</span>
            <span className="text-slate-500 truncate">— {scrapeRun.progress?.message}</span>
          </div>
          {scrapeRun.status === 'awaiting_2fa' && (
            <div className="flex gap-1.5 mt-1.5">
              <input value={twofa} onChange={(e) => setTwofa(e.target.value)} placeholder="Verification code"
                className="flex-1 border border-slate-300 rounded px-2 py-1 text-[11px]" data-testid="migration-card-2fa-input" />
              <Button size="sm" className="h-6 text-[10px] rounded-none bg-amber-600 hover:bg-amber-700" onClick={submit2fa} data-testid="migration-card-2fa-submit">Submit</Button>
            </div>
          )}
        </div>
      )}

      {journey.status === 'complete' && (
        <div data-testid="migration-outcome-complete" className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" />
          <div className="text-xs text-slate-700">
            <div className="font-semibold text-emerald-800">Migration onboarded</div>
            <div>{created.locations || 0} properties, {created.spaces || 0} units, {created.vendor_accounts || 0} vendor accounts, {created.meters || 0} meters — reversible from the Migration page. See it live under <span className="font-medium">Org → UBM Onboarding</span>.</div>
          </div>
        </div>
      )}

      {/* Credit burn (transparency) */}
      {credits && credits.total_credits > 0 && (
        <div className="text-[10px] text-slate-400" data-testid="migration-card-credits">
          AI usage so far: <span className="font-semibold text-slate-600">{credits.total_credits} credits</span> (≈ ${(credits.total_credits * 0.10).toFixed(2)}) — preview, not yet billed
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px] flex-1" data-testid="migration-card-open-review"
          onClick={() => { window.location.href = `/ubm/migration/${journeyId}`; }}>
          <ExternalLink className="h-3 w-3 mr-1" /> Review data
        </Button>
        {journey.status === 'review' && (counts.all?.included || 0) > 0 && (
          <Button size="sm" className="rounded-none h-7 text-[11px] flex-1 bg-orange-600 hover:bg-orange-700" data-testid="migration-card-approve"
            disabled={approving} onClick={approve}>
            {approving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3 w-3 mr-1" />}
            Approve &amp; Onboard
          </Button>
        )}
      </div>
    </div>,
  );
};

export default MigrationJourneyCard;
